import type { ShoppingCart } from '../../api/types/shoppingCart';
import type { AirBooking } from '../../api/types/air';
import type { ReservationReportRow } from '../../api/types/reports';

/**
 * `Entry/Find` bir tek `ticket`/`pnr` ile çağrıldığında yalnız o kaydın satırını döndürür.
 * Çok noktalı (multi-city) bir rezervasyonda her bacak ayrı `AirBooking` + ayrı PNR + ayrı
 * bilet numarasıdır (bkz. [[api-contract]] / flowConfig.ts `shouldUseMultiCityEndpoint`).
 * Bu yardımcı, sayfaya zaten gelen `GetShoppingCart` yanıtından her bacağın bilet satırını
 * (`ReservationDetailPage`'in `ReservationRowCard`'ları için) yeniden kurar — ikinci bir
 * API çağrısına gerek kalmadan. Sepette bilgi olmayan alanlar (`username`, `paymentTypes`,
 * `cRS` …) doldurulmaz; tıklanan bacağın tam `Entry/Find` satırı çağıran tarafta bunların
 * üzerine bindirilir.
 *
 * Yalnız `airBookings.length > 1` iken anlamlıdır; tek booking'de boş döner ki mevcut
 * tek-satır davranışı değişmesin.
 */
/** ISO tarih-saati "DD.MM.YYYY" biçimine çevirir (row `departureDate` alanı `Entry/Find`'da
 *  bu biçimde geliyor, olduğu gibi gösteriliyor). Geçersizse `null`. */
function toRowDate(iso?: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

export function reservationRowsFromCart(
  cart: ShoppingCart | null | undefined
): ReservationReportRow[] {
  const airBookings = (cart?.airBookings ?? []) as AirBooking[];
  if (!cart || airBookings.length <= 1) return [];

  const paxByRefId = new Map<string, { firstName?: string; lastName?: string }>();
  (cart.passengers ?? []).forEach((p) => {
    if (p.paxReferanceId) paxByRefId.set(p.paxReferanceId, { firstName: p.firstName, lastName: p.lastName });
  });

  const rows: ReservationReportRow[] = [];
  airBookings.forEach((booking) => {
    const segments = booking.flightSegments ?? [];
    const first = segments[0];
    const last = segments[segments.length - 1];
    const timeTable = booking.airBookingTimeTable ?? undefined;

    (booking.passengerBreakdowns ?? []).forEach((pb) => {
      const refId = pb.paxReference?.paxReferanceId;
      const pax = refId ? paxByRefId.get(refId) : undefined;
      rows.push({
        shoppingCartId: cart.id,
        pnr: booking.bookingCode,
        documentNumber: pb.ticketNumber,
        statu: booking.status,
        provider: booking.provider,
        providerType: 'Air',
        firstName: pax?.firstName,
        lastName: pax?.lastName,
        origin: first?.departureAirportCode ?? null,
        destination: last?.arrivalAirportCode ?? null,
        departureDate: toRowDate(first?.departureDateTime),
        returnDate: null,
        bookedAt: timeTable?.bookingDate || undefined,
        prebookedAt: timeTable?.prebookingDate || undefined,
        expireAt: timeTable?.prebookingExpiresAt || undefined,
        baseFare: pb.baseFare ?? booking.baseFare,
        totalTax: pb.totalTax ?? booking.totalTax,
        totalServiceFee: pb.totalServiceFee ?? booking.totalServiceFee,
        totalFare: pb.totalFare ?? booking.totalFare,
      });
    });
  });
  return rows;
}

/**
 * Sepetten üretilen satırlar tıklanan `Entry/Find` sonucundan fazlaysa (= eksik bacak var)
 * onları kullan; tıklanan bilete/PNR'a denk gelen satırda tam `Entry/Find` verisini koru.
 * Aksi halde taban sonucu değiştirme.
 */
export function mergeCartRows(
  baseRows: ReservationReportRow[],
  cartRows: ReservationReportRow[]
): ReservationReportRow[] {
  if (cartRows.length <= baseRows.length) return baseRows;
  return cartRows.map((cr) => {
    const match = baseRows.find(
      (br) =>
        (!!br.documentNumber && br.documentNumber === cr.documentNumber) ||
        (!!br.pnr && br.pnr === cr.pnr && !cr.documentNumber)
    );
    return match ?? cr;
  });
}
